/** @jsxImportSource @emotion/react */

import { FC, useMemo } from 'react'
import { Form as FinalForm } from 'react-final-form'
import { useMutation, useQuery } from '@apollo/client'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import { FieldArray } from 'react-final-form-arrays'
import { Button } from '@mui/material'
import { FormApi } from 'final-form'
import { CMSScreenshotPages } from '__generatedTypes__/globalTypes'
import {
  CREATE_SCREENSHOT_FEEDBACK,
  FIND_ONE_SCREENSHOT_FEEDBACK_BY_PAGE,
  UPDATE_SCREENSHOT_FEEDBACK,
} from 'queries/screenshotFeedback'
import Loader from 'components/Loader'
import { ImageField } from 'components/DropzoneField'
import { validateRequired } from 'utils/validation'
import { InputField } from 'components/Input'
import { setFieldData } from 'utils/setFieldData'
import { useImageUpload } from 'utils/saveImageS3'
import { COMMON_ELEMENTS_PATH_S3, PUBLIC_PREFIX } from 'constants/images'
import {
  createScreenshotFeedback,
  createScreenshotFeedbackVariables,
} from '__generatedTypes__/createScreenshotFeedback'
import {
  updateScreenshotFeedback,
  updateScreenshotFeedbackVariables,
} from '__generatedTypes__/updateScreenshotFeedback'
import {
  findOneScreenshotFeedbackByPage,
  findOneScreenshotFeedbackByPageVariables,
} from '__generatedTypes__/findOneScreenshotFeedbackByPage'
import { toast } from 'utils/toast'
import TextareaAutosize from 'components/TextareaAutosize/TextareaAutosize'
import { ScreenshotFieldArray } from '../ScreenshotFieldArray'
import * as classes from './styles'

type Screenshot = Array<Partial<ImageField> | undefined>

export type FormValues = {
  page: CMSScreenshotPages
  title: string
  description: string
  screenshots: Screenshot[]
  id?: string
}

type Props = {
  platformId: string
  platformSlug: string
  page: CMSScreenshotPages
}

const focusOnErrors = createDecorator<FormValues, Partial<FormValues>>()

export const Form: FC<Props> = ({ platformId, platformSlug, page }) => {
  const { data, loading, refetch } = useQuery<
    findOneScreenshotFeedbackByPage,
    findOneScreenshotFeedbackByPageVariables
  >(FIND_ONE_SCREENSHOT_FEEDBACK_BY_PAGE, {
    variables: {
      page,
      platformSlug,
    },
  })

  const [createScreenshotFeedback] = useMutation<createScreenshotFeedback, createScreenshotFeedbackVariables>(
    CREATE_SCREENSHOT_FEEDBACK
  )

  const [updateScreenshotFeedback] = useMutation<updateScreenshotFeedback, updateScreenshotFeedbackVariables>(
    UPDATE_SCREENSHOT_FEEDBACK
  )

  const uploadImage = useImageUpload()

  const screenshotFeedback = data?.findOneScreenshotFeedbackByPage

  const uploadFiles = async (images: Screenshot[]) => {
    const createScreenshot = async (file: File, pathS3: string) => {
      const path = await uploadImage(file, pathS3)

      return `${process.env.REACT_APP_IMAGEKIT_URL}/tr:h-default,w-default/${PUBLIC_PREFIX}/${path}`
    }

    const uploadedImages = await Promise.all(
      images.map(([image]) => {
        if (image?.file && !image.isUploaded) {
          return createScreenshot(image.file, COMMON_ELEMENTS_PATH_S3)
        }

        return image?.initialValue || ''
      })
    )

    return uploadedImages
  }

  const initialValues = useMemo(
    () =>
      screenshotFeedback
        ? {
            page: screenshotFeedback.page,
            title: screenshotFeedback.title,
            description: screenshotFeedback.description,
            screenshots: screenshotFeedback.images.map((screenshot) => [
              {
                isUploaded: true,
                src: screenshot?.url || '',
                initialValue: screenshot?.url || '',
              },
            ]),
            id: screenshotFeedback.id,
          }
        : {
            screenshots: [[]],
          },
    [screenshotFeedback?.id]
  )

  const handleSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
    const { id, title, description, screenshots } = values

    try {
      const images = await uploadFiles(screenshots)

      const input = {
        id,
        page,
        title,
        description,
        images,
        platform: {
          id: platformId,
        },
      }
      await (id
        ? updateScreenshotFeedback({
            variables: {
              updateScreenshotFeedbackInputFieldName: input,
            },
          })
        : createScreenshotFeedback({
            variables: {
              createScreenshotFeedbackInput: input,
            },
          }))

      form.change(
        'screenshots',
        screenshots.map((screenshot, i) => {
          const screenshotEntity = screenshot[0]
          return [{ ...screenshotEntity, isUploaded: true, initialValue: images[i] || '' }]
        })
      )
      refetch()

      toast.success('Changes saved successfully')
    } catch (error) {
      toast.error('Failed to save changes')
    }
  }

  if (loading) {
    return <Loader />
  }

  return (
    <FinalForm<FormValues>
      mutators={{ ...arrayMutators, setFieldData }}
      decorators={[focusOnErrors]}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, submitting }) => (
        <form onSubmit={handleSubmit} css={classes.form}>
          <InputField name="title" validate={validateRequired} label="Title" placeholder="Enter Title" fullWidth />
          <TextareaAutosize
            name="description"
            validate={validateRequired}
            label="Description"
            placeholder="Enter Description"
          />

          <FieldArray name="screenshots">
            {({ fields, meta }) => <ScreenshotFieldArray fields={fields} meta={meta} />}
          </FieldArray>
          <Button variant="contained" color="blue" size="large" type="submit" fullWidth disabled={submitting}>
            Save Edits
          </Button>
        </form>
      )}
    />
  )
}
